'use client';

import { useMemo } from 'react';
import * as echarts from 'echarts';
import { useTheme } from 'next-themes';
import dayjs from 'dayjs';
import { range } from 'lodash';
import { currencyFormatter } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import type { MarketPriceHistoryResult } from '@/hooks/use-markets-price-history';
import { PolymarketMarket } from './types';
import EChart from '@/components/common/EChart';
import { formatDateTime } from '@/lib/utils/date';

const VOLUME_SERIES_KEY = 'Volume';
const VOLUME_LINE_COLOR = '#D9D9D92E';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * 按 X 轴实际跨度挑日期格式。
 *
 * 原来固定用 'MMMD'：市场只开了几小时时，每个刻度印出来都是同一天，
 * 看着就是一排重复文字；跨度上到一年，'MMM D' 又会挤成一团。
 * 刻度数量由 ECharts 按容器宽度自己算，这里只负责让每个刻度印得下、且有区分度。
 */
function pickTimeAxisFormat(spanMs: number): string {
  if (!Number.isFinite(spanMs) || spanMs <= 0) return 'MMM D';
  if (spanMs <= 2 * DAY_MS) return 'HH:mm';
  if (spanMs <= 180 * DAY_MS) return 'MMM D';
  return 'MMM YYYY';
}

export default function MarketTrendChart({
  markets,
  openInSidebar,
  isTimePointChart,
  historiesData,
  isLoadingHistories = false,
}: {
  markets: PolymarketMarket[];
  openInSidebar?: boolean;
  isTimePointChart?: boolean;
  historiesData: MarketPriceHistoryResult[];
  isLoadingHistories?: boolean;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const volumeSeriesName = t(VOLUME_SERIES_KEY);

  const marketOptions = useMemo(
    () =>
      [...markets].sort((a, b) => toNumber(b.outcome_yes_price) - toNumber(a.outcome_yes_price)),
    [markets],
  );

  // 准备图表数据
  const chartMarkets = useMemo(() => {
    if (!marketOptions || !historiesData) return [];

    return marketOptions
      .map((market) => {
        const history = historiesData.find(
          (item) => item.request.market === market.clob_tokens_id_yes,
        );

        // 更新最后一项的 probability 值
        let dataPoints =
          history?.history.map((point) => ({
            timestamp: dayjs.unix(point.t).toISOString(),
            probability: point.p.toString(),
            volume: point.v,
          })) || [];

        if (dataPoints.length > 0) {
          const lastProbability = confirmLastProbability(
            market,
            +(dataPoints.at(-1)?.probability || 0),
          );
          dataPoints[dataPoints.length - 1] = {
            ...dataPoints[dataPoints.length - 1],
            probability: lastProbability.toString(),
          };
        }

        if (dataPoints.length === 0) {
          dataPoints = buildFallbackDataPoints(market);
        }

        return {
          market_id: market.market_id,
          question: market.market_question,
          data_points: dataPoints,
          title: market.group_item_title || t('YES'),
        };
      })
      .filter((m) => m.data_points.length > 0);
  }, [marketOptions, historiesData, t]);

  const volumeData = useMemo(() => {
    const volumeByTimestamp = new Map<number, number>();

    for (const item of historiesData) {
      for (const point of item.history) {
        volumeByTimestamp.set(point.t, (volumeByTimestamp.get(point.t) ?? 0) + point.v);
      }
    }

    return [...volumeByTimestamp.entries()]
      .sort(([leftTimestamp], [rightTimestamp]) => leftTimestamp - rightTimestamp)
      .map(([timestamp, volume]) => [timestamp * 1000, volume] as [number, number]);
  }, [historiesData]);

  const legendData = useMemo(
    () => chartMarkets.map((m) => m.title).filter(Boolean),
    [chartMarkets],
  );

  const option = useMemo<echarts.EChartsOption | null>(() => {
    if (chartMarkets.length === 0 && volumeData.length === 0) return null;

    const ECHARTS_DEFAULT_COLORS = [
      '#5470c6',
      '#91cc75',
      '#fac858',
      '#ee6666',
      '#73c0de',
      '#3ba272',
      '#fc8452',
      '#9a60b4',
      '#ea7ccc',
    ];

    let lineEndDate = 0;
    const series: echarts.LineSeriesOption[] = chartMarkets.map((item, index) => {
      const dataPoints = item.data_points;
      lineEndDate = Math.max(
        lineEndDate,
        ...item.data_points.map((item) => dayjs(item.timestamp).valueOf()),
      );
      const color =
        index >= 0 ? ECHARTS_DEFAULT_COLORS[index % ECHARTS_DEFAULT_COLORS.length] : '#999999';
      return {
        data: dataPoints.map((m) => [m.timestamp, +m.probability * 100]),
        name: item.title,
        type: 'line',
        showSymbol: false,
        showAllSymbol: 'auto',
        symbol: 'circle',
        symbolSize: 7,
        smooth: true,
        gridIndex: 0,
        itemStyle: { color },
        lineStyle: { width: 1.5 },
      };
    }) as echarts.LineSeriesOption[];

    const seriesLastPoints = series.map((s) => {
      const data = (s.data as [string, number][]) || [];
      const last = data.at(-1);
      return {
        name: s.name,
        date: last ? dayjs(last[0]).format('YYYY-MM-DD HH:mm') : null,
        value: last ? +last[1].toFixed(2) : null,
        color: s.itemStyle?.color,
      };
    });

    const allValues = series.flatMap((s) =>
      ((s.data as [string, number][]) || []).map(([, v]) => v),
    );
    const yMax = allValues.length ? Math.ceil(Math.max(...allValues)) : 100;
    const volumeTimes = volumeData.map(([timestamp]) => timestamp).filter(Number.isFinite);

    let scatterSeriesList: echarts.ScatterSeriesOption[] = [];
    let lineSeries: echarts.LineSeriesOption[] = [];
    const seriesTimes = series
      .flatMap((item) =>
        ((item.data as [string, number][]) || []).map(([timestamp]) => dayjs(timestamp).valueOf()),
      )
      .filter(Number.isFinite);
    const allTimes = [...seriesTimes, ...volumeTimes];
    const xAxisMin = allTimes.length > 0 ? Math.min(...allTimes) : undefined;
    const xAxisMax = allTimes.length > 0 ? Math.max(...allTimes) : undefined;
    // 两个时间轴（概率区 / 成交量区）共用同一套刻度格式，避免上下对不上
    const timeLabelFormat = pickTimeAxisFormat((xAxisMax ?? 0) - (xAxisMin ?? 0));
    let xAxis: echarts.EChartsOption['xAxis'] = [
      {
        type: 'time',
        gridIndex: 0,
        min: Number.isFinite(xAxisMin) ? xAxisMin : undefined,
        max: Number.isFinite(xAxisMax) ? xAxisMax : undefined,
        axisLabel: {
          show: !isTimePointChart,
          color: '#757575',
          // 容器变窄（如抽屉内）或刻度变多时，让 ECharts 自己丢掉放不下的标签，
          // 而不是硬挤在一起
          hideOverlap: true,
          formatter: (value: number) => formatDateTime(value, timeLabelFormat),
        },
      },
    ];
    let yAxis: echarts.EChartsOption['yAxis'] = [
      {
        type: 'value',
        position: 'right',
        min: 0,
        max: yMax,
        splitLine: {
          show: true,
          lineStyle: { color: theme === 'dark' ? '#2c2c2f' : '#e9e9e9', type: 'dashed' },
        },
        axisLabel: { color: '#757575', formatter: (v) => `${v}%` },
        gridIndex: 0,
      },
    ];
    let grid: echarts.EChartsOption['grid'] = [
      {
        left: 0,
        right: 0,
        top: 70,
        height: 200,
      },
    ];
    if (isTimePointChart) {
      const scatterData = chartMarkets
        .map((m) => {
          const market = chartMarkets.find((item) => item.market_id === m.market_id);
          if (market) {
            return [m.title, +(market.data_points.at(-1)?.probability || 0) * 100];
          }
          return undefined;
        })
        .filter(Boolean);
      const scatterPoints = scatterData
        .map((point) => {
          if (!point) return null;
          const [title, value, outcome] = point as [string, number, string];
          const seriesIdx = chartMarkets.findIndex((fm) => fm.title === title);
          const color =
            seriesIdx >= 0
              ? ECHARTS_DEFAULT_COLORS[seriesIdx % ECHARTS_DEFAULT_COLORS.length]
              : '#999999';
          return {
            value: [title, value, outcome],
            itemStyle: { color },
          };
        })
        .filter(
          (
            point,
          ): point is {
            value: [string, number, string];
            itemStyle: { color: string };
          } => Boolean(point),
        );

      scatterSeriesList = scatterPoints.map((point) => ({
        data: [point],
        type: 'scatter' as const,
        gridIndex: 1,
        xAxisIndex: 1,
        yAxisIndex: 1,
        label: {
          position: 'top',
          show: true,
          color: 'inherit',
          fontSize: 9,
          formatter: (p: any) => {
            // return p.value[2];
            // console.log('scatterSeriesList:', p);
            return `${p.value[1].toFixed(1)}%`;
          },
        },
      }));
      const xData = [
        lineEndDate ? formatDateTime(lineEndDate, 'MMMM D') : '',
        ...scatterPoints.map((point) => point?.value?.[0]),
      ].filter(Boolean);
      lineSeries = seriesLastPoints.map((item, index) => {
        return {
          name: item.name,
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 1,
          gridIndex: 1,
          showSymbol: false,
          lineStyle: { type: 'dashed', width: 1 },
          data: range(0, index + 2).map((_) => item.value),
          itemStyle: { color: item.color },
          tooltip: { show: false },
        };
      }) as echarts.LineSeriesOption[];
      xAxis.push({
        type: 'category',
        gridIndex: 1,
        boundaryGap: false,
        data: xData as string[],
        axisLabel: {
          color: '#757575',
        },
      });
      yAxis = yAxis.map((item) => ({ ...item, axisLabel: { formatter: '' } }));
      yAxis.push({
        show: true,
        splitLine: {
          show: true,
          lineStyle: { color: theme === 'dark' ? '#2c2c2f' : '#e9e9e9', type: 'dashed' },
        },
        axisLabel: { color: '#757575', formatter: (v) => `${v}%` },
        type: 'value',
        min: 0,
        max: yMax,
        gridIndex: 1,
        position: 'right',
      });
      grid = [
        {
          left: 0,
          right: '30%',
          top: 70,
          height: 200,
          containLabel: false,
        },
        {
          left: '70.3%',
          right: '0',
          top: 70,
          height: 200,
          containLabel: false,
        },
      ];
    }

    const volumeGridIndex = Array.isArray(grid) ? grid.length : 1;
    const volumeXAxisIndex = Array.isArray(xAxis) ? xAxis.length : 1;
    const volumeYAxisIndex = Array.isArray(yAxis) ? yAxis.length : 1;

    grid = [
      ...(Array.isArray(grid) ? grid : [grid]),
      {
        left: 0,
        right: isTimePointChart ? '30%' : 40,
        top: 300,
        height: 96,
        containLabel: false,
      },
    ];
    xAxis = [
      ...(Array.isArray(xAxis) ? xAxis : [xAxis]),
      {
        type: 'time',
        gridIndex: volumeGridIndex,
        min: Number.isFinite(xAxisMin) ? xAxisMin : undefined,
        max: Number.isFinite(xAxisMax) ? xAxisMax : undefined,
        axisLabel: {
          color: '#757575',
          hideOverlap: true,
          formatter: (value: number) => formatDateTime(value, timeLabelFormat),
        },
        axisTick: {
          show: false,
        },
      },
    ];
    yAxis = [
      ...(Array.isArray(yAxis) ? yAxis : [yAxis]),
      {
        type: 'value',
        show: false,
        gridIndex: volumeGridIndex,
      },
    ];

    const volumeSeries: echarts.LineSeriesOption = {
      name: volumeSeriesName,
      type: 'line',
      showSymbol: false,
      showAllSymbol: 'auto',
      symbol: 'circle',
      symbolSize: 7,
      smooth: true,
      data: volumeData,
      xAxisIndex: volumeXAxisIndex,
      yAxisIndex: volumeYAxisIndex,
      areaStyle: {},
      itemStyle: { color: VOLUME_LINE_COLOR },
      lineStyle: { color: VOLUME_LINE_COLOR, width: 1.5 },
    };

    const option: echarts.EChartsOption = {
      legend: {
        show: true,
        data: legendData,
        textStyle: {
          color: '#B7B9BC',
          fontSize: 12,
        },
        icon: 'circle',
        itemWidth: 6,
        itemHeight: 6,
        itemGap: 16,
        left: 0,
        top: 10,
        padding: [0, 200, 0, 0],
      },
      grid,
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(39, 39, 39, 0.9)',
        borderColor: '#4D4D4D',
        textStyle: { color: '#fff' },
        appendTo: 'body',
        formatter: (params: any) => formatTooltip(params, series, volumeData, volumeSeriesName),
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: 'rgba(255,255,255,0.42)',
            width: 1,
            type: 'dashed',
          },
        },
      },
      axisPointer: {
        link: [{ xAxisIndex: isTimePointChart ? [0, volumeXAxisIndex] : 'all' }],
        lineStyle: {
          color: 'rgba(255,255,255,0.42)',
          width: 1,
          type: 'dashed',
        },
      },
      xAxis,
      yAxis,
      series: [...series, ...scatterSeriesList, ...lineSeries, volumeSeries],
      animation: true,
      animationDuration: 300,
    };

    return option;
  }, [chartMarkets, isTimePointChart, legendData, theme, volumeData, volumeSeriesName]);

  return (
    <EChart
      option={option}
      loading={isLoadingHistories}
      empty={chartMarkets.length === 0 && volumeData.length === 0}
      className="min-h-96"
      wrapperClassName="min-h-96"
      openInSidebar={openInSidebar}
    />
  );
}

function formatTooltip(
  params: any,
  probabilitySeries: echarts.LineSeriesOption[],
  volumeData: [number, number][],
  volumeSeriesName: string,
) {
  const items = Array.isArray(params) ? params : [params];
  const axisValue = items.find((item: any) => item?.axisValue !== undefined)?.axisValue;
  const timestamp = resolveTooltipTimestamp(axisValue, items);
  const title = Number.isFinite(timestamp)
    ? dayjs(timestamp).format('YYYY-MM-DD HH:mm')
    : items[0]?.axisValueLabel || items[0]?.name || '';
  const probabilityRows = probabilitySeries
    .map((series) => {
      const point = findNearestTuple(series.data as [string | number, number][], timestamp);
      if (!point) return null;

      const value = Number(point[1]);
      const color =
        typeof series.itemStyle?.color === 'string' ? series.itemStyle.color : '#999999';

      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;min-width:170px;">
        <span style="display:flex;align-items:center;color:#b8b8b8;">
          <span style="background-color:${color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:6px;"></span>
          ${series.name}
        </span>
        <span>${Number.isFinite(value) ? value.toFixed(1) : '-'}%</span>
      </div>`;
    })
    .filter(Boolean)
    .join('');
  const volumePoint = findNearestTuple(volumeData, timestamp);
  const volumeValue = Number(volumePoint?.[1]);
  const volumeRow = `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;min-width:170px;">
    <span style="display:flex;align-items:center;color:#b8b8b8;">
      <span style="background-color:${VOLUME_LINE_COLOR};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:6px;"></span>
      ${volumeSeriesName}
    </span>
    <span>${currencyFormatter(Number.isFinite(volumeValue) ? volumeValue : 0)}</span>
  </div>`;
  const rows = `${probabilityRows}${volumeRow}`;

  return `<div>${title}<br/>${rows}</div>`;
}

function resolveTooltipTimestamp(axisValue: unknown, items: any[]) {
  const fromAxis = typeof axisValue === 'number' ? axisValue : dayjs(axisValue as string).valueOf();
  if (Number.isFinite(fromAxis)) return fromAxis;

  for (const item of items) {
    const value = Array.isArray(item?.value) ? item.value[0] : undefined;
    const timestamp = typeof value === 'number' ? value : dayjs(value).valueOf();
    if (Number.isFinite(timestamp)) return timestamp;
  }

  return Number.NaN;
}

function findNearestTuple<T extends [string | number, number]>(
  data: T[] | undefined,
  timestamp: number,
) {
  if (!data?.length || !Number.isFinite(timestamp)) return null;

  return data.reduce<T | null>((nearest, item) => {
    const itemTimestamp = typeof item[0] === 'number' ? item[0] : dayjs(item[0]).valueOf();
    if (!Number.isFinite(itemTimestamp)) return nearest;
    if (!nearest) return item;

    const nearestTimestamp =
      typeof nearest[0] === 'number' ? nearest[0] : dayjs(nearest[0]).valueOf();

    return Math.abs(itemTimestamp - timestamp) < Math.abs(nearestTimestamp - timestamp)
      ? item
      : nearest;
  }, null);
}

function toNumber(value: string | number | null | undefined) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildFallbackDataPoints(market: PolymarketMarket) {
  const probability = confirmLastProbability(market, toNumber(market.outcome_yes_price));
  const endTime = resolveFallbackEndTime(market);
  const startTime = resolveFallbackStartTime(market, endTime);

  return [
    {
      timestamp: startTime.toISOString(),
      probability: probability.toString(),
      volume: 0,
    },
    {
      timestamp: endTime.toISOString(),
      probability: probability.toString(),
      volume: 0,
    },
  ];
}

function resolveFallbackStartTime(market: PolymarketMarket, endTime: dayjs.Dayjs) {
  const startTime = dayjs(market.created_at || market.start_date_iso);

  if (startTime.isValid() && startTime.isBefore(endTime)) {
    return startTime;
  }

  return endTime.subtract(1, 'day');
}

function resolveFallbackEndTime(market: PolymarketMarket) {
  if (market.market_closed || market.uma_resolution_status === 'resolved') {
    const closedTime = dayjs(market.closed_time ?? market.end_date);
    if (closedTime.isValid()) return closedTime;
  }

  return dayjs();
}

function confirmLastProbability(market: PolymarketMarket, probability: number) {
  if (market.uma_resolution_status === 'resolved' || market.market_closed) {
    return +market.outcome_yes_price > 0.5 ? 1 : 0;
  }
  return probability;
}
